import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { ICaaCustomerAddress } from "@repo/shopify/queries/customer-account-gql";
import type { MailingAddress } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import Button from "@atoms/Button";
import Dim from "@atoms/Dim";
import Icon from "@atoms/Icon";

import AddressForm from "@organisms/Address/AddressForm";
import AddressFormIntl from "@organisms/Address/AddressFormIntl";
import AddressFormJp from "@organisms/Address/AddressFormJp";
import AddressItem from "@organisms/Address/AddressItem";
import AddressItemJp from "@organisms/Address/AddressItemJp";

import useAddresses from "@hooks/useAddresses";
import useLanguage from "@hooks/useLanguage";

import { useLayoutStore } from "@store/layout";

export default function Addresses() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { showFooter } = useLayoutStore();
  const isJp = lang === "jp";

  const { isPC } = useCheckDevice();

  const {
    showForm,
    setShowForm,
    editableAddress,
    setEditableAddress,
    onCompletedMutation,
    openAddressForm,
    defaultAddress,
    otherAddresses,
  } = useAddresses(isJp);

  useEffect(() => {
    showFooter(true);
    window.scrollTo(0, 0);
  }, []);

  const handleCancelForm = () => {
    setShowForm(false);
    setEditableAddress(undefined);
  };

  const handleFormCompleted = async () => {
    await onCompletedMutation();
    setShowForm(false);
    setEditableAddress(undefined);
  };

  return (
    <>
      <section className="pc:col-span-10 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10">
        <div className="pc:col-span-3 col-span-8 flex h-fit flex-col gap-5">
          {isPC ? <h2 className="text-title">Addresses</h2> : null}
          <div className="flex flex-col gap-2.5">
            <p className="leading-5">
              <Trans i18nKey="addresses.description" />
            </p>
            <Button
              as="square"
              className="w-fit text-[14px]!"
              onClick={() => {
                window.scrollTo(0, 0);
                setShowForm(true);
              }}
              variant="secondary"
            >
              {t("addresses.create_new_address")}
            </Button>
          </div>
        </div>

        <div className="pc:col-span-5 col-span-8 grid grid-cols-subgrid">
          {defaultAddress ? (
            <div className="pc:col-span-5 pc:gap-y-0 border-grey col-span-8 grid grid-cols-subgrid gap-y-2.5 border-b pb-5">
              {isJp ? (
                <AddressItemJp
                  address={defaultAddress as ICaaCustomerAddress}
                  isDefaultAddress
                  onCompletedMutation={onCompletedMutation}
                  openAddressForm={openAddressForm}
                />
              ) : (
                <AddressItem
                  address={defaultAddress as MailingAddress}
                  isDefaultAddress
                  onCompletedMutation={onCompletedMutation}
                  openAddressForm={openAddressForm}
                />
              )}
            </div>
          ) : null}

          {otherAddresses.length ? (
            <ul className="tnt-gap-x pc:col-span-5 col-span-8 grid grid-cols-subgrid gap-y-10 pt-5">
              {otherAddresses.map((node) => (
                <li className="pc:col-span-5 col-span-8 grid grid-cols-subgrid gap-y-2.5" key={node.id}>
                  {isJp ? (
                    <AddressItemJp
                      address={node as ICaaCustomerAddress}
                      isDefaultAddress={defaultAddress?.id === node.id}
                      onCompletedMutation={onCompletedMutation}
                      openAddressForm={openAddressForm}
                    />
                  ) : (
                    <AddressItem
                      address={node as MailingAddress}
                      isDefaultAddress={defaultAddress?.id === node.id}
                      onCompletedMutation={onCompletedMutation}
                      openAddressForm={openAddressForm}
                    />
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      {showForm ? (
        <>
          <Dim onClick={handleCancelForm} />
          <section className="pc:h-dvh pc:max-h-dvh pc:w-1/3 pc:pt-4.5 pc:p-5 pc:z-50 pc:gap-5 fixed top-0 right-0 z-40 flex h-dvh w-full flex-col bg-white p-4 pt-0 lg:w-1/4 dark:text-white">
            {isPC ? (
              <div className="flex w-full justify-between">
                <span>Address Book</span>
                <Button as="text" onClick={handleCancelForm}>
                  Close
                </Button>
              </div>
            ) : (
              <div className="tnt-gap-x grid grid-cols-8 items-center py-5">
                <Button as="wrapper" className="col-span-1" onClick={handleCancelForm}>
                  <Icon className="h-3.5 dark:text-white" name="LeftArrow" />
                </Button>
                <Button as="text" className="col-span-7 text-base!" onClick={handleCancelForm}>
                  Back
                </Button>
              </div>
            )}

            <div className="col-span-8 flex flex-col gap-5">
              <div className="flex flex-col gap-2.5">
                <h2 className="col-span-12 text-base!">{editableAddress ? "Edit a Address" : "Add a New Address"}</h2>
              </div>
              {lang === "ko" && (
                <AddressForm
                  address={editableAddress as MailingAddress | undefined}
                  isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                  onClickCancel={handleCancelForm}
                  onCompletedMutation={handleFormCompleted}
                />
              )}
              {lang === "en" && (
                <AddressFormIntl
                  address={editableAddress as MailingAddress | undefined}
                  className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5"
                  isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                  isEmptyAddress={!defaultAddress?.id}
                  onClickCancel={handleCancelForm}
                  onCompletedMutation={handleFormCompleted}
                />
              )}
              {lang === "jp" && (
                <AddressFormJp
                  address={editableAddress as ICaaCustomerAddress | undefined}
                  className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5"
                  isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                  isEmptyAddress={!defaultAddress?.id}
                  onClickCancel={handleCancelForm}
                  onCompletedMutation={handleFormCompleted}
                />
              )}
            </div>
          </section>
        </>
      ) : null}
    </>
  );
}
